import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { App, Button, Card, DatePicker, Empty, Flex, Form, Input, InputNumber, Modal, Radio, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { Link } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, useSearchFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PlatformLabel } from "../channels/PlatformBadge";
import type { Case } from "./common";

type Claim = Schemas["AftersalesClaim"];
type Status = Claim["status"];
const STATUS_COLOR: Record<Status, string> = { submitted: "gold", approved: "processing", rejected: "default", paid: "success" };

export function ClaimStatusTag({ status }: { status: Status }) {
  const { t } = useI18n();
  return <Tag color={STATUS_COLOR[status]}>{t(`claim.status.${status}`)}</Tag>;
}

function useClaimsManage() {
  const { can } = useAuth();
  return can("orders.manage", "finance.manage");
}

/** Klaim of a case (Plan §6.22): to the marketplace or a courier, with what was paid. */
export function ClaimsCard({ c, onDone }: { c: Case; onDone: (c: Case) => void }) {
  const { t, formatMoney, formatDate } = useI18n();
  const manage = useClaimsManage();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Claim | null>(null);
  if (c.claims.length === 0 && (!manage || c.status === "cancelled")) return null;
  return (
    <Card
      size="small"
      title={t("claim.title")}
      extra={
        manage &&
        c.status !== "cancelled" && (
          <Button size="small" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
            {t("claim.add")}
          </Button>
        )
      }
    >
      {c.claims.length === 0 ? (
        <Typography.Text type="secondary">{t("claim.none")}</Typography.Text>
      ) : (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          {c.claims.map((cl) => (
            <Flex key={cl.id} justify="space-between" align="start" gap={8}>
              <Space orientation="vertical" size={0}>
                <Space size={6} wrap>
                  <Typography.Text strong>{cl.against === "courier" ? cl.carrier : t("claim.against.marketplace")}</Typography.Text>
                  <ClaimStatusTag status={cl.status} />
                </Space>
                <Typography.Text className="z-num">
                  {formatMoney(cl.amount)}
                  {cl.status === "paid" && cl.paidAmount != null && ` → ${t("claim.paidOn", { amount: formatMoney(cl.paidAmount), date: formatDate(cl.paidOn!) })}`}
                </Typography.Text>
                {(cl.reference || cl.note) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {[cl.reference, cl.note].filter(Boolean).join(" · ")}
                  </Typography.Text>
                )}
              </Space>
              {manage && cl.status !== "paid" && cl.status !== "rejected" && (
                <Button size="small" onClick={() => setEditing(cl)}>
                  {t("claim.update")}
                </Button>
              )}
            </Flex>
          ))}
        </Space>
      )}
      {adding && <NewClaimModal c={c} onClose={() => setAdding(false)} onDone={onDone} />}
      {editing && <ClaimUpdateModal claim={editing} onClose={() => setEditing(null)} onDone={onDone} />}
    </Card>
  );
}

function NewClaimModal({ c, onClose, onDone }: { c: Case; onClose: () => void; onDone: (c: Case) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [form] = Form.useForm<{ against: Claim["against"]; carrier?: string; reference?: string; amount?: number; note?: string }>();
  const against = Form.useWatch("against", form);
  const save = useMutation({
    mutationFn: (v: { against: Claim["against"]; carrier?: string; reference?: string; amount?: number; note?: string }) =>
      unwrap(
        api.POST("/v1/aftersales/{caseID}/claims", {
          params: { path: { caseID: c.id } },
          body: { against: v.against, carrier: v.carrier ?? "", reference: v.reference ?? "", amount: v.amount!, note: v.note ?? "" },
        }),
      ),
    onSuccess: (x) => {
      onDone(x);
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal open title={t("claim.add")} okText={t("claim.submit")} cancelText={t("common.cancel")} confirmLoading={save.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ against: "marketplace", carrier: c.returnCarrier || undefined }}
        onFinish={(v) => save.mutate(v)}
      >
        <Form.Item name="against" label={t("claim.against")}>
          <Radio.Group
            options={[
              { value: "marketplace", label: t("claim.against.marketplace") },
              { value: "courier", label: t("claim.against.courier") },
            ]}
          />
        </Form.Item>
        {against === "courier" && (
          <Form.Item name="carrier" label={t("claim.carrier")} rules={[{ required: true, whitespace: true, message: t("field.required") }]}>
            <Input maxLength={100} />
          </Form.Item>
        )}
        <Form.Item name="amount" label={t("claim.amount")} rules={[{ required: true, message: t("field.required") }]}>
          <InputNumber min={1} precision={0} prefix="Rp" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="reference" label={`${t("claim.reference")} ${t("common.optional")}`}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="note" label={`${t("claim.note")} ${t("common.optional")}`}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

type UpdateValues = { status: Status; reference?: string; amount: number; paidAmount?: number; paidOn?: Dayjs; note?: string };

function ClaimUpdateModal({ claim, onClose, onDone }: { claim: Claim; onClose: () => void; onDone: (c: Case) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [form] = Form.useForm<UpdateValues>();
  const status = Form.useWatch("status", form);
  const save = useMutation({
    mutationFn: (v: UpdateValues) =>
      unwrap(
        api.PUT("/v1/aftersales/claims/{claimID}", {
          params: { path: { claimID: claim.id } },
          body: {
            status: v.status,
            reference: v.reference?.trim() ?? "",
            amount: v.amount,
            paidAmount: v.status === "paid" ? (v.paidAmount ?? null) : null,
            paidOn: v.status === "paid" && v.paidOn ? v.paidOn.format("YYYY-MM-DD") : null,
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    onSuccess: (x) => {
      onDone(x);
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal open title={t("claim.update")} okText={t("common.save")} cancelText={t("common.cancel")} confirmLoading={save.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ status: claim.status, reference: claim.reference, amount: claim.amount, paidAmount: claim.amount, paidOn: dayjs(), note: claim.note }}
        onFinish={(v) => save.mutate(v)}
      >
        <Form.Item name="status" label={t("common.status")}>
          <Select options={(["submitted", "approved", "rejected", "paid"] as const).map((s) => ({ value: s, label: t(`claim.status.${s}`) }))} />
        </Form.Item>
        <Form.Item name="amount" label={t("claim.amount")} rules={[{ required: true, message: t("field.required") }]}>
          <InputNumber min={1} precision={0} prefix="Rp" style={{ width: 200 }} />
        </Form.Item>
        {status === "paid" && (
          <Flex gap={16}>
            <Form.Item name="paidAmount" label={t("claim.paidAmount")} rules={[{ required: true, message: t("field.required") }]}>
              <InputNumber min={0} precision={0} prefix="Rp" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="paidOn" label={t("claim.paidOnLabel")} rules={[{ required: true, message: t("field.required") }]}>
              <DatePicker />
            </Form.Item>
          </Flex>
        )}
        <Form.Item name="reference" label={`${t("claim.reference")} ${t("common.optional")}`}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="note" label={`${t("claim.note")} ${t("common.optional")}`} extra={status === "rejected" || status === "paid" ? t("claim.final") : undefined}>
          <Input.TextArea rows={2} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Purna jual > Klaim: every claim, what is still waiting and what was paid. */
export function Claims() {
  const { t, formatMoney, formatNumber, formatDate, formatDateTime } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [status, setStatus] = useUrlFilter<string>("status", "submitted,approved", ["submitted,approved", "paid", "rejected", ""]);
  const filters = { ...(status ? { status } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["aftersales", "claims", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/aftersales/claims", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space size={16} wrap>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("claim.pageTitle")}
          </Typography.Title>
          {list.data && list.data.openClaims > 0 && (
            <Typography.Text type="secondary">{t("claim.openSummary", { n: formatNumber(list.data.openClaims), amount: formatMoney(list.data.openAmount) })}</Typography.Text>
          )}
        </Space>
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={status}
          onChange={setStatus}
          options={[
            { value: "submitted,approved", label: t("claim.filter.open") },
            { value: "paid", label: t("claim.status.paid") },
            { value: "rejected", label: t("claim.status.rejected") },
            { value: "", label: t("claim.filter.all") },
          ]}
        />
        <SearchFilter filter={search} placeholder={t("claim.search")} />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/aftersales/claims/export"
          query={filters}
          filename={t("claim.pageTitle")}
          columns={[
            { key: "createdAt", label: t("claim.at"), kind: "datetime" },
            { key: "caseNumber", label: t("claim.case") },
            { key: "orderNumber", label: t("as.col.order") },
            { key: "shopName", label: t("orders.store") },
            { key: "against", label: t("claim.against"), values: { marketplace: t("claim.against.marketplace"), courier: t("claim.against.courier") } },
            { key: "carrier", label: t("claim.carrier") },
            { key: "reference", label: t("claim.reference") },
            { key: "amount", label: t("claim.amount"), kind: "number" },
            { key: "status", label: t("common.status"), values: Object.fromEntries((["submitted", "approved", "rejected", "paid"] as const).map((s) => [s, t(`claim.status.${s}`)])) },
            { key: "paidAmount", label: t("claim.paidAmount"), kind: "number" },
            { key: "paidOn", label: t("claim.paidOnLabel") },
            { key: "note", label: t("claim.note") },
          ]}
        />
      </Flex>
      <Table<Claim>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: <Empty description={t("claim.empty")} /> }}
        scroll={{ x: 900 }}
        columns={[
          { title: t("claim.at"), dataIndex: "createdAt", width: 160, render: (v: string) => formatDateTime(v, tz) },
          {
            title: t("claim.case"),
            render: (_, x) => (
              <Space orientation="vertical" size={0}>
                <Link to={`/app/aftersales/${x.caseId}`} className="z-num">
                  {x.caseNumber}
                </Link>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {x.platform && <PlatformLabel platform={x.platform as never} />} {x.shopName} · <span className="z-num">{x.orderNumber}</span>
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("claim.against"), width: 150, render: (_, x) => (x.against === "courier" ? x.carrier : t("claim.against.marketplace")) },
          { title: t("claim.amount"), dataIndex: "amount", width: 140, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
          { title: t("common.status"), dataIndex: "status", width: 130, render: (s: Status) => <ClaimStatusTag status={s} /> },
          {
            title: t("claim.paidAmount"),
            width: 170,
            align: "right",
            render: (_, x) => (x.paidAmount != null ? <span className="z-num">{`${formatMoney(x.paidAmount)} · ${formatDate(x.paidOn!)}`}</span> : "—"),
          },
          { title: t("claim.reference"), dataIndex: "reference", width: 150, render: (v: string) => v || "—" },
        ]}
      />
    </>
  );
}
